import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaTimes, FaPrint } from 'react-icons/fa';
import api from '../api';
import { theme, t } from '../theme';
import { imprimirEtiquetas } from '../platform/etiqueta';
import { copiasParaFornada, semVinculoDeProduto, ROTULO_CONSERVACAO } from './etiquetaValidade';

/**
 * Impressão de etiqueta.
 *
 * TRÊS ORIGENS, UM FORMULÁRIO: ficha técnica (o brigadeiro que ela acabou de
 * fazer), produto do estoque (a embalagem que ela acabou de abrir) e avulsa (o
 * o sistema não conhece). Separar em três telas faria a confeiteira escolher
 * um caminho antes de saber o que quer etiquetar.
 *
 * NADA DE PLACEHOLDER QUE PARECE VALOR. A primeira versão mostrava a sugestão
 * de validade como placeholder cinza: era impossível saber, olhando, se o "5"
 * já estava preenchido ou se era só um exemplo — e essa dúvida, num campo de
 * data de validade de alimento, custa caro. Agora o que é sugestão vem como
 * VALOR de verdade (escuro, editável) e o que é exemplo começa com "ex.:".
 *
 * A VALIDADE É CALCULADA NO SERVIDOR. O preview aqui é só previsão; quem grava
 * a data é o backend, porque um relógio adiantado no computador da cozinha não
 * pode virar data errada numa embalagem de comida.
 */
const hoje = () => new Date().toISOString().slice(0, 10);

const somarDias = (dias) => {
  const d = new Date();
  d.setDate(d.getDate() + Number(dias || 0));
  return d.toISOString().slice(0, 10);
};

const paraBR = (iso) => {
  if (!iso) return '';
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a}`;
};

const EtiquetaModal = ({ inicial, onFechar, onCriada }) => {
  const [padroes, setPadroes] = useState(null);
  const [fichas, setFichas] = useState([]);
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);

  // 'dias' é o normal (a preparação dura N dias); 'data' existe porque às vezes
  // o prazo vem de fora — uma encomenda com dia certo para sair. E 'fabricante'
  // porque nem tudo passa a durar menos depois de aberto: açúcar, farinha e
  // arroz seguem valendo a data da embalagem, e forçar um prazo curto ali
  // encheria o painel de validade de alarme falso.
  // Abre no modo que o backend sugeriu. Para embalagem aberta de item seco a
  // sugestao e "A do fabricante": abrir o acucar nao faz ele durar menos, e
  // forcar um prazo curto ali encheria o painel de validade de alarme falso.
  // Sem data do fabricante para usar, a sugestao volta a ser por dias.
  const [modoValidade, setModoValidade] = useState(
    inicial?.modo_validade === 'fabricante' && inicial?.validade_original ? 'fabricante' : 'dias',
  );
  const [validadeData, setValidadeData] = useState('');

  const [form, setForm] = useState(() => ({
    origem: 'avulsa',
    nome: '',
    conservacao: 'refrigerado',
    quantidade: '',
    unidade_medida: 'g',
    unidades: 1,
    responsavel: '',
    prazo_dias: '',
    acompanhar_validade: true,
    observacoes: '',
    ficha_tecnica_id: '',
    produto_id: null,
    lote_id: null,
    lote_texto: '',
    ...(inicial || {}),
  }));

  const mudar = (campo) => (e) => {
    const valor = e && e.target
      ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value)
      : e;
    setForm((f) => ({ ...f, [campo]: valor }));
  };

  useEffect(() => {
    api.get('/api/etiquetas/padroes').then((r) => setPadroes(r.data)).catch(() => {});
    api.get('/fichas/api/fichas-tecnicas')
      .then((r) => setFichas(r.data?.fichas || []))
      .catch(() => {});
  }, []);

  // A sugestão entra como VALOR, não como placeholder — e só quando o campo
  // ainda está vazio, para nunca sobrescrever o que a pessoa digitou.
  useEffect(() => {
    if (!padroes) return;
    setForm((f) => (f.prazo_dias === '' || f.prazo_dias == null
      ? { ...f, prazo_dias: String(padroes.prazos?.[f.conservacao] ?? '') }
      : f));
  }, [padroes]);

  const trocarConservacao = (e) => {
    const nova = e.target.value;
    setForm((f) => ({
      ...f,
      conservacao: nova,
      // Trocar de refrigerado para congelado sem o prazo acompanhar deixaria
      // "5 dias" num congelado — o erro mais fácil de cometer aqui.
      prazo_dias: String(padroes?.prazos?.[nova] ?? f.prazo_dias),
    }));
  };

  const carregarSugestao = useCallback(async (fichaId) => {
    if (!fichaId) return;
    try {
      const r = await api.get('/api/etiquetas/sugestao', { params: { ficha_tecnica_id: fichaId } });
      setForm((f) => ({
        ...semVinculoDeProduto(f),
        origem: 'ficha',
        ficha_tecnica_id: fichaId,
        nome: r.data.nome,
        conservacao: r.data.conservacao,
        quantidade: r.data.quantidade != null ? String(r.data.quantidade) : '',
        unidade_medida: r.data.unidade_medida || 'g',
        prazo_dias: String(r.data.prazo_dias),
      }));
      // Preparação não tem data de fabricante — se o modal estava nesse modo
      // por causa de um produto, ele ficaria apontando para nada.
      setModoValidade((m) => (m === 'fabricante' ? 'dias' : m));
    } catch { /* a pessoa preenche na mão; não vale travar a impressão por isso */ }
  }, []);

  // Previsão, só para conferir antes de gastar papel.
  const previsaoISO = useMemo(() => {
    if (modoValidade === 'fabricante') return form.validade_original;
    if (modoValidade === 'data') return validadeData;
    return somarDias(form.prazo_dias);
  }, [modoValidade, validadeData, form.prazo_dias, form.validade_original]);

  // Só existe quando a etiqueta veio do estoque e o lote tinha data do fabricante.
  // Trocar o lote troca as DUAS coisas que vao para o papel: o numero impresso
  // no rodape e a validade do fabricante. Atualizar so uma delas imprimiria o
  // lote de uma embalagem com a data de outra.
  const trocarLote = (ev) => {
    const id = ev.target.value;
    const lote = (form.lotes || []).find((l) => String(l.id) === String(id));
    setForm((f) => ({
      ...f,
      lote_id: id,
      lote_texto: lote?.numero_lote || '',
      validade_original: lote?.validade || null,
    }));
    // Se o lote escolhido nao tem data do fabricante, o modo "A do fabricante"
    // ficaria apontando para nada e a etiqueta sairia sem validade nenhuma.
    if (!lote?.validade) setModoValidade((m) => (m === 'fabricante' ? 'dias' : m));
  };

  const temFabricante = Boolean(form.validade_original);

  const imprimir = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) { setErro('Dê um nome para a etiqueta.'); return; }
    if (modoValidade === 'data' && !validadeData) { setErro('Escolha a data de validade.'); return; }

    setSalvando(true); setErro(null);
    try {
      const { data: etiqueta } = await api.post('/api/etiquetas', {
        ...form,
        quantidade: form.quantidade === '' ? null : Number(String(form.quantidade).replace(',', '.')),
        unidades: Number(form.unidades) || 1,
        ...(modoValidade === 'fabricante'
          ? { validade_em: form.validade_original }
          : modoValidade === 'data'
            ? { validade_em: validadeData }
            : { prazo_dias: form.prazo_dias === '' ? null : Number(form.prazo_dias) }),
      });
      // Uma fornada de 12 vira 12 papéis com o mesmo QR — um job de impressão.
      await imprimirEtiquetas(copiasParaFornada(etiqueta), `etiqueta-${etiqueta.codigo}.pdf`);
      if (onCriada) onCriada(etiqueta);
      onFechar();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Não consegui criar a etiqueta.');
    } finally {
      setSalvando(false);
    }
  };

  const conservacoes = padroes?.conservacoes || Object.keys(ROTULO_CONSERVACAO);
  const quantasEtiquetas = Number(form.unidades) || 1;
  const abaValidade = (id, rotulo) => (
    <button type="button" key={id}
      onClick={() => {
        setModoValidade(id);
        if (id === 'data' && !validadeData) setValidadeData(somarDias(form.prazo_dias || 0));
      }}
      style={{
        border: `1px solid ${modoValidade === id ? theme.colors.primary : theme.colors.border}`,
        background: modoValidade === id ? theme.colors.primary : '#fff',
        color: modoValidade === id ? '#fff' : theme.colors.textSecondary,
        borderRadius: 8, padding: '4px 10px', fontSize: 12, cursor: 'pointer',
      }}>
      {rotulo}
    </button>
  );

  return (
    <div style={t.overlay} className="modal-overlay-mobile">
      <div style={{ ...t.modal, maxWidth: 520, maxHeight: '92vh', overflowY: 'auto' }}
        className="modal-content-sheet">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Nova etiqueta</h3>
            <p style={{ margin: '4px 0 0', color: '#999', fontSize: 13 }}>
              Imprime em etiqueta de 60 × 40 mm
            </p>
          </div>
          <button onClick={onFechar} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', fontSize: 20 }}>
            <FaTimes />
          </button>
        </div>

        {form.origem === 'produto' && (
          <div style={{ background: '#f4f8fb', border: '1px solid #d6e4f0', borderRadius: 10, padding: '10px 12px', marginBottom: 14, fontSize: 13, color: '#31708f' }}>
            Etiqueta de <strong>embalagem aberta</strong>. A validade do fabricante fica
            registrada, e a que vale agora é a de depois de aberto.
          </div>
        )}

        {/* Só aparece com mais de um lote em estoque. Com um só, perguntar seria
            uma escolha sem alternativa; com vários, o padrão é o que vence
            primeiro (o que o FIFO consome), mas quem sabe qual embalagem abriu é
            quem está com ela na mão — e essa data vai impressa no papel. */}
        {form.origem === 'produto' && (form.lotes || []).length > 1 && (
          <div style={{ marginBottom: 12 }}>
            <label style={t.label}>Qual embalagem você abriu?</label>
            <select value={form.lote_id || ''} onChange={trocarLote} style={t.input}>
              {form.lotes.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.numero_lote || 'sem lote'}
                  {l.validade ? ` · vence ${paraBR(l.validade)}` : ' · sem validade'}
                  {l.quantidade != null ? ` · ${l.quantidade} em estoque` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <form onSubmit={imprimir}>
          <div style={{ marginBottom: 12 }}>
            <label style={t.label}>Ficha técnica (opcional)</label>
            <select value={form.ficha_tecnica_id || ''}
              onChange={(e) => (e.target.value ? carregarSugestao(e.target.value)
                : setForm((f) => ({ ...semVinculoDeProduto(f), ficha_tecnica_id: '', origem: 'avulsa' })))}
              style={t.input}>
              <option value="">Etiqueta avulsa — digito o nome</option>
              {fichas.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={t.label}>Nome *</label>
            <input value={form.nome} onChange={mudar('nome')} style={t.input}
              placeholder="ex.: Brigadeiro de Ninho" maxLength={200} />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={t.label}>Conservação *</label>
            <select value={form.conservacao} onChange={trocarConservacao} style={t.input}>
              {conservacoes.map((c) => (
                <option key={c} value={c}>{ROTULO_CONSERVACAO[c] || c}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <label style={{ ...t.label, marginBottom: 0 }}>Validade *</label>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {abaValidade('dias', 'Por dias')}
                {abaValidade('data', 'Até a data')}
                {temFabricante && abaValidade('fabricante', 'A do fabricante')}
              </div>
            </div>
            {modoValidade === 'dias' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="number" min="0" value={form.prazo_dias} onChange={mudar('prazo_dias')}
                  style={{ ...t.input, maxWidth: 110 }} />
                <span style={{ fontSize: 13, color: theme.colors.textSecondary }}>
                  dias {padroes?.prazos?.[form.conservacao] != null
                    && `(sugerido para ${(ROTULO_CONSERVACAO[form.conservacao] || '').toLowerCase()}: ${padroes.prazos[form.conservacao]})`}
                </span>
              </div>
            ) : modoValidade === 'fabricante' ? (
              <div style={{ fontSize: 13, color: theme.colors.textSecondary, padding: '8px 0' }}>
                Vale a data da embalagem: <strong>{paraBR(form.validade_original)}</strong>
                <span style={{ display: 'block', color: '#aaa', fontSize: 12 }}>
                  Para o que não dura menos depois de aberto — açúcar, farinha, arroz.
                </span>
              </div>
            ) : (
              <input type="date" value={validadeData} min={hoje()}
                onChange={(e) => setValidadeData(e.target.value)} style={t.input} />
            )}
          </div>

          {/* Quantidade é POR EMBALAGEM. Sem dizer isso, "800 g" e "12" na
              mesma linha se leem como 800 g no total divididos em 12 — e a
              etiqueta sairia mentindo sobre o que tem dentro. */}
          <div style={{ marginBottom: 4 }}>
            <label style={t.label}>Peso / Quantidade</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 8 }}>
              <input value={form.quantidade} onChange={mudar('quantidade')} style={t.input}
                placeholder="ex.: 800" inputMode="decimal" />
              <select value={form.unidade_medida} onChange={mudar('unidade_medida')} style={t.input}>
                {['g', 'kg', 'ml', 'l', 'un', 'porções'].map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={t.label}>Número de etiquetas</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="number" min="1" max="60" value={form.unidades}
                onChange={mudar('unidades')} style={{ ...t.input, maxWidth: 110 }} />
              <span style={{ fontSize: 13, color: theme.colors.textSecondary }}>
                {quantasEtiquetas > 1
                  ? `${quantasEtiquetas} etiquetas iguais para o mesmo insumo — dar baixa em uma desconta do total`
                  : 'uma etiqueta'}
              </span>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={t.label}>Quem manipulou</label>
            <input value={form.responsavel} onChange={mudar('responsavel')} style={t.input}
              placeholder="ex.: Maria" maxLength={100} />
          </div>

          {form.origem === 'avulsa' && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.acompanhar_validade}
                onChange={mudar('acompanhar_validade')} style={{ marginTop: 3 }} />
              <span style={{ fontSize: 13, color: theme.colors.textSecondary }}>
                Acompanhar no painel de validade
                <span style={{ display: 'block', color: '#aaa', fontSize: 12 }}>
                  {form.acompanhar_validade
                    ? 'Vai aparecer no painel e nos avisos de vencimento.'
                    : 'Só imprime: não entra no painel nem nos avisos.'}
                </span>
              </span>
            </label>
          )}

          {previsaoISO && (
            <div style={{ background: '#eafaf1', border: '1px solid #a9dfbf', borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#1e8449', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Vai valer até
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text }}>
                {paraBR(previsaoISO)}
                {form.quantidade && (
                  <span style={{ fontSize: 13, fontWeight: 500, color: '#7f8c8d' }}>
                    {' '}· {quantasEtiquetas > 1 ? `${quantasEtiquetas} × ` : ''}{form.quantidade}{form.unidade_medida}
                  </span>
                )}
              </div>
            </div>
          )}

          {erro && (
            <div style={{ background: '#fdecea', color: '#c0392b', padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
              {erro}
            </div>
          )}

          <button type="submit" disabled={salvando}
            style={{ ...t.btnPrimary, width: '100%', justifyContent: 'center', padding: '12px 0' }}>
            <FaPrint /> {salvando ? 'Gerando...' : `Imprimir ${quantasEtiquetas > 1 ? `${quantasEtiquetas} etiquetas` : 'etiqueta'}`}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EtiquetaModal;
